import { computed } from 'vue'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { LoginInput, UserClaims } from '~/types/gql/generated/graphql'
import { APP_ROUTES } from '~/constants'
import { LoginMutation } from '~/queries/auth/login.mutation'
import { RegenerateTokenGqlMutation } from '~/queries/misc/regenerate-token.mutation'
import { useLoginAttempts } from '~/composables/useLoginAttempts'

export const useSessionStore = defineStore('session', () => {
  const queryClient = useQueryClient()
  const { requester } = useRequester()
  const router = useRouter()
  const { token, setToken, decodedToken } = useSession()
  const { resetLoginAttempts, incrementLoginAttempts } = useLoginAttempts()

  const currentUser = computed<UserClaims | null>({
    get() {
      return decodedToken.value?.payload?.value
    },
    set(value) {
      if (value === null)
        setToken(null)
    },
  })

  const isAuthenticated = computed(() => !!currentUser.value && !!token.value)
  const isAdmin = computed(() => !!currentUser.value?.isAdmin)

  async function logout() {
    await router.push(APP_ROUTES.login.path)
    await nextTick()
    setToken(null)
    queryClient.removeQueries()
    resetLoginAttempts()
  }

  const { mutateAsync: login } = useMutation({
    mutationFn: async (loginInfo: LoginInput) => requester(LoginMutation, { loginInfo }),
    onSuccess: async (data) => {
      incrementLoginAttempts()
      const _token = data.login?.accessToken
      if (_token) {
        setToken(_token)
        await nextTick()
      }
    },
  })

  const { mutateAsync: _regenerateTokenAsync } = useMutation({
    mutationFn: async (_selectedOrganisationId?: number) => {
      const selectedOrganisationId = _selectedOrganisationId ?? currentUser.value?.currentOrganisationId
      return requester(RegenerateTokenGqlMutation, { selectedOrganisationId: selectedOrganisationId! })
    },
    onSuccess: async (data) => {
      const _token = data.regenerateToken

      if (_token) {
        setToken(_token)
        await nextTick()
      }
    },
  })

  function regenerateTokenAsync(selectedOrganisationId?: number) {
    return _regenerateTokenAsync(selectedOrganisationId)
  }

  return {
    isAuthenticated,
    currentUser,
    isAdmin,
    token,
    logout,
    login,
    regenerateTokenAsync,
  }
})
